/* ==========================================================================
   ZETA SEARCH 検索ヘッダーコントローラ
   ==========================================================================

   snippets/predictive-search.liquid のドロワー(zeta-search-drawer)を制御する。

   構成は Figma 305-2590(検索Drawer)に準拠する。上から:
   - [C] トレンドキーワード: ホットワードAPI。入力欄直下の**チップ行**。常時表示
         (作業者メモ 検索課題No.54「トレンドキーワード…ランキングからサジェストを表示」)
   - [B] サジェスト: 入力中のみ(200ms debounce)。**見出しなしのフラットリスト**。
         0件かつ4文字以上なら「もしかして」候補を同じリストで出す
         (作業者メモ 検索課題No.55「部分一致でサジェストを出してほしい」)
   - [A] キーワード履歴: ログイン時のみ。未入力時は直近5件、入力中は前方一致 最大3件
         (Backlog合意: APIは全件取得→フロントで前方一致抽出)
   - Enter/検索ボタンは通常のフォームGET送信(/search?q=)。履歴保存は結果ページ側(§5.11)
   - サジェスト・ホットワードのAPIエラー時は該当ブロックを非表示(エラー表示はしない)

   実装ポリシー(実装仕様書 §1.2): 単一state・テンプレートリテラル描画・
   escapeHtml徹底・イベント委譲・API呼び出しは ZetaSearchAPI に一元化。
   ========================================================================== */

(() => {
  const config = window.zetaSearchConfig || {};
  const api = window.ZetaSearchAPI;
  const DrawerContent = customElements.get('drawer-content');
  if (!api || !DrawerContent || customElements.get('zeta-search-drawer')) return;

  const limits = config.limits || {};

  // HTMLへ埋め込む文字列は必ずこれを通す(属性値も同様)
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  const searchUrl = (keyword, category) => category
    ? `/collections/${encodeURIComponent(category)}?q=${encodeURIComponent(keyword)}`
    : `${config.searchUrl}?q=${encodeURIComponent(keyword)}`;

  class ZetaSearchDrawer extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();

      this.blocks = {
        history: this.querySelector('[data-zeta-block="history"]'),
        suggest: this.querySelector('[data-zeta-block="suggest"]'),
        hotword: this.querySelector('[data-zeta-block="hotword"]')
      };
      this.iconClose = this.querySelector('template[data-zeta-template="icon-close"]')?.innerHTML.trim() || '';

      // 単一の状態オブジェクト。histories / hotwords はセッション中メモリキャッシュ(§6.3)
      this.state = {
        input: '',
        histories: null,   // null = 未取得
        hotwords: null,
        suggests: [],
        didyoumean: []
      };
      this.suggestAbortController = null;

      this.delegate.on('input', '[name="q"]', this._debounce(this._onInput.bind(this), 200));
      this.delegate.on('click', '[data-zeta-keyword]', this._onKeywordClick.bind(this));
      this.delegate.on('click', '[data-zeta-delete-history]', this._onDeleteHistory.bind(this));
      this.delegate.on('click', '[data-zeta-clear-history]', this._onClearHistory.bind(this));
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      // ドロワー初回オープンで履歴・急上昇ワードを取得(§6.3)
      if (name === 'open' && this.open && !this._openedOnce) {
        this._openedOnce = true;
        this._loadInitial();
      }
    }

    /* ------------------------------------------------------------
       データ取得
       ------------------------------------------------------------ */

    _loadInitial() {
      api.hotwords(limits.hotword || 10)
        .then((response) => { this.state.hotwords = response?.keyword || []; })
        .catch(() => { this.state.hotwords = []; }) // エラー時はブロック非表示
        .finally(() => this._render());

      if (config.userId) {
        api.getKeywordHistory(config.userId)
          .then((response) => { this.state.histories = response?.histories || []; })
          .catch(() => { this.state.histories = []; })
          .finally(() => this._render());
      } else {
        this.state.histories = []; // 未ログイン時は履歴を表示しない(履歴APIは呼ばない)
      }
    }

    async _onInput(event, target) {
      const value = target.value.trim();
      this.state.input = value;
      this.suggestAbortController?.abort();

      if (!value) {
        // 入力が空になったら未入力状態表示に戻す
        this.state.suggests = [];
        this.state.didyoumean = [];
        this._render();
        return;
      }

      this.suggestAbortController = new AbortController();
      const { signal } = this.suggestAbortController;
      const q = value.slice(0, 20); // suggest / didyoumean は 20文字以下に切り詰め

      try {
        const response = await api.suggest(q, limits.suggest || 10, signal);
        this.state.suggests = response?.keyword || [];
        this.state.didyoumean = [];

        // サジェスト0件かつ4文字以上のときのみ「もしかして」を取得(§6.5)
        if (!this.state.suggests.length && q.length >= 4) {
          const didyoumean = await api.didYouMean(q, undefined, signal);
          this.state.didyoumean = didyoumean?.keyword || [];
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.state.suggests = [];
        this.state.didyoumean = [];
      }
      this._render();
    }

    /* ------------------------------------------------------------
       イベント
       ------------------------------------------------------------ */

    _onKeywordClick(event, target) {
      const { keyword, category } = target.dataset;
      if (!keyword) return;
      window.location.assign(searchUrl(keyword, category));
    }

    _onDeleteHistory(event, target) {
      event.stopPropagation();
      const id = target.dataset.historyId;
      api.deleteKeywordHistory(id)
        .then(() => {
          this.state.histories = (this.state.histories || []).filter((history) => history.id !== id);
          this._render();
        })
        .catch(() => {});
    }

    _onClearHistory() {
      api.clearKeywordHistory(config.userId)
        .then(() => {
          this.state.histories = [];
          this._render();
        })
        .catch(() => {});
    }

    /* ------------------------------------------------------------
       レンダリング(state → HTML文字列 → innerHTML)
       ------------------------------------------------------------ */

    _render() {
      this._renderHistory();
      this._renderSuggest();
      this._renderHotword();
    }

    /*
      キーワード1行。Figma は見出しを持たず、区切り線だけのフラットなリスト。
      trailing には履歴の削除ボタンなど行末の要素を渡す。
     */
    _keywordRow(keyword, { category = '', trailing = '' } = {}) {
      return `
        <li class="zeta-suggest-block__item">
          <button type="button" class="zeta-suggest-block__keyword" data-zeta-keyword
            data-keyword="${escapeHtml(keyword)}" data-category="${escapeHtml(category)}">
            ${escapeHtml(keyword)}
          </button>
          ${trailing}
        </li>`;
    }

    _keywordList(items) {
      return `<ul class="zeta-suggest-block__list" role="list">${items.join('')}</ul>`;
    }

    _historyItem(history) {
      // category 付き履歴はカテゴリ内検索であることを示す(名称取得は§9-9、第一段階はワードのみ)
      const categoryNote = history.category ? '<span class="zeta-suggest-block__category">(カテゴリ内)</span>' : '';
      const trailing = `
        <button type="button" class="zeta-suggest-block__delete" data-zeta-delete-history
          data-history-id="${escapeHtml(history.id)}" aria-label="履歴を削除">
          ${this.iconClose}
        </button>`;
      return `
        <li class="zeta-suggest-block__item">
          <button type="button" class="zeta-suggest-block__keyword" data-zeta-keyword
            data-keyword="${escapeHtml(history.keyword)}" data-category="${escapeHtml(history.category || '')}">
            ${escapeHtml(history.keyword)}${categoryNote}
          </button>
          ${trailing}
        </li>`;
    }

    _renderHistory() {
      const block = this.blocks.history;
      const histories = this.state.histories;
      if (!config.userId || !histories?.length) {
        block.hidden = true;
        block.innerHTML = '';
        return;
      }

      let items;
      let clearButton = '';
      if (this.state.input) {
        // 入力中: 前方一致した履歴を最大3件(Backlog合意)
        items = histories
          .filter((history) => history.keyword.startsWith(this.state.input))
          .slice(0, limits.historyOnTyping || 3);
      } else {
        // 未入力時: 直近5件+全削除リンク
        items = histories.slice(0, limits.historyOnFocus || 5);
        clearButton = '<button type="button" class="zeta-suggest-block__clear link text--subdued" data-zeta-clear-history>すべて削除</button>';
      }

      if (!items.length) {
        block.hidden = true;
        block.innerHTML = '';
        return;
      }

      // Figma は見出しを持たないため、未入力時の「すべて削除」だけを右寄せで出す
      block.innerHTML = `
        ${clearButton ? `<div class="zeta-suggest-block__header">${clearButton}</div>` : ''}
        ${this._keywordList(items.map((history) => this._historyItem(history)))}`;
      block.hidden = false;
    }

    _renderSuggest() {
      const block = this.blocks.suggest;
      const { input, suggests, didyoumean } = this.state;
      if (!input || (!suggests.length && !didyoumean.length)) {
        block.hidden = true;
        block.innerHTML = '';
        return;
      }

      /*
        Figma は候補を見出しなしのフラットなリストで並べる。
        サジェスト0件時の「もしかして」候補(§6.5)も同じ見た目で出す。
       */
      const entries = suggests.length ? suggests : didyoumean;
      block.innerHTML = this._keywordList(entries.map((entry) => this._keywordRow(entry.label)));
      block.hidden = false;
    }

    _renderHotword() {
      const block = this.blocks.hotword;
      const hotwords = this.state.hotwords;
      if (!hotwords?.length) {
        block.hidden = true;
        block.innerHTML = '';
        return;
      }
      /*
        Figma 305-2590 では入力欄直下に折り返しのチップ行として並ぶ(見出しなし)。
        チップの見た目は一覧側のタイムライン・サジェストと同じ .zeta-chip を共用する。
        クリックでそのワードのワード検索結果へ遷移(要件どおり)。
       */
      block.innerHTML = `
        <div class="zeta-suggest-chips zeta-suggest-chips--wrap">
          ${hotwords.map((hotword) => `
            <button type="button" class="zeta-chip" data-zeta-keyword
              data-keyword="${escapeHtml(hotword.label)}">${escapeHtml(hotword.label)}</button>`).join('')}
        </div>`;
      block.hidden = false;
    }

    _debounce(fn, delay) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  customElements.define('zeta-search-drawer', ZetaSearchDrawer);
})();
